import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import EmailContextVariableSelectModal from "./EmailContextVariableSelectModal";
import {
  applyEmailTemplate,
  buildContextPreviewValues,
  formatContextPlaceholder,
  insertTextAtCaret,
  type EmailTemplate,
} from "@/utils/emailTemplate";

type EmailTemplateEditModalProps = {
  isOpen: boolean;
  template: EmailTemplate | null;
  companyId?: number | null;
  isSystemScope?: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type BodyField = "htmlBody" | "textBody";

const EMPTY_TEMPLATE: EmailTemplate = {
  id: 0,
  identifier: "",
  name: "",
  subject: "",
  htmlBody: "",
  textBody: "",
  parameterSchema: "[]",
  isActive: true,
};

export default function EmailTemplateEditModal(props: EmailTemplateEditModalProps) {
  const [form, setForm] = useState<EmailTemplate>(EMPTY_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const [variablePickerField, setVariablePickerField] = useState<BodyField | null>(null);
  const htmlSplitRef = useRef<HTMLDivElement | null>(null);
  const htmlTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const textTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const previewValues = useMemo(
    () => buildContextPreviewValues(form.subject, form.htmlBody, form.textBody),
    [form.subject, form.htmlBody, form.textBody]
  );

  const previewHtml = useMemo(
    () => applyEmailTemplate(form.htmlBody || "", previewValues),
    [form.htmlBody, previewValues]
  );

  useEffect(() => {
    if (!props.isOpen) return;
    const next = props.template ? { ...EMPTY_TEMPLATE, ...props.template } : { ...EMPTY_TEMPLATE };
    setForm(next);
    setLeftPanelWidth(50);
    setVariablePickerField(null);
  }, [props.isOpen, props.template]);

  useEffect(() => {
    if (!htmlSplitRef.current) return;
    htmlSplitRef.current.style.setProperty("--content-edit-left-width", `${leftPanelWidth}%`);
  }, [leftPanelWidth, props.isOpen]);

  function updateField(field: keyof EmailTemplate, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function getTextareaRef(field: BodyField) {
    return field === "htmlBody" ? htmlTextareaRef : textTextareaRef;
  }

  function insertPlaceholder(field: BodyField, source: string) {
    const placeholder = formatContextPlaceholder(source);
    if (!placeholder) return;

    const textarea = getTextareaRef(field).current;
    const currentValue = field === "htmlBody" ? form.htmlBody || "" : form.textBody || "";
    insertTextAtCaret(textarea, placeholder, currentValue, (next) => updateField(field, next));
  }

  function openVariablePicker(field: BodyField) {
    getTextareaRef(field).current?.focus();
    setVariablePickerField(field);
  }

  function handleVariableSelected(source: string) {
    if (!variablePickerField) return;
    insertPlaceholder(variablePickerField, source);
    setVariablePickerField(null);
  }

  function onSplitterMouseDown(event: ReactMouseEvent<HTMLDivElement>) {
    event.preventDefault();

    const onMouseMove = (moveEvent: MouseEvent) => {
      const container = htmlSplitRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const offsetX = moveEvent.clientX - rect.left;
      const percentage = (offsetX / rect.width) * 100;
      setLeftPanelWidth(Math.min(75, Math.max(25, percentage)));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  async function saveAsync() {
    if (!form.identifier?.trim() || !form.name?.trim()) {
      toast.error("Identifier and name are required.");
      return;
    }

    const payload: EmailTemplate = {
      ...form,
      parameterSchema: "[]",
    };

    setSaving(true);
    try {
      let resp;
      if (props.isSystemScope || !props.companyId) {
        if (payload.id) {
          resp = await RightTimeApi.putAsync(`/EmailTemplate/${payload.id}`, payload);
        } else {
          resp = await RightTimeApi.postAsync("/EmailTemplate", payload);
        }
      } else if (payload.id) {
        resp = await RightTimeApi.putAsync(`/EmailTemplate/${payload.id}`, payload);
      } else {
        resp = await RightTimeApi.postAsync(`/EmailTemplate/Company/${props.companyId}`, payload);
      }

      if (resp?.ok) {
        toast.success("Email template saved.");
        props.onSaved();
        props.onClose();
      } else {
        toast.error("Failed to save email template.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save email template.");
    } finally {
      setSaving(false);
    }
  }

  if (!props.isOpen) return null;

  return (
    <>
      <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        title={form.id ? "Edit Email Template" : "Add Email Template"}
        className="emailTemplateEditModal"
      >
        <div className="emailTemplateForm factTable">
          <table>
            <tbody>
              <tr>
                <th className="label right">Identifier</th>
                <td>
                  <input
                    type="text"
                    title="Template identifier"
                    placeholder="e.g. test-created"
                    value={form.identifier || ""}
                    onChange={(e) => updateField("identifier", e.target.value)}
                    disabled={!!form.id && form.scope === "system" && !props.isSystemScope}
                  />
                </td>
              </tr>
              <tr>
                <th className="label right">Name</th>
                <td>
                  <input
                    type="text"
                    title="Template name"
                    placeholder="Display name"
                    value={form.name || ""}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th className="label right">Subject</th>
                <td>
                  <input
                    type="text"
                    title="Email subject"
                    placeholder="Subject line"
                    value={form.subject || ""}
                    onChange={(e) => updateField("subject", e.target.value)}
                  />
                </td>
              </tr>
              <tr className="emailTemplateHtmlRow">
                <th className="label right">HTML Body</th>
                <td>
                  <div ref={htmlSplitRef} className="emailTemplateHtmlGrid contentEditGrid active">
                    <div className="contentEditSection contentEditSectionRaw">
                      <div className="emailTemplateSectionHeader">
                        <h4>HTML Source</h4>
                        <button type="button" className="emailSelectVariableBtn" onClick={() => openVariablePicker("htmlBody")}>
                          Select Variable
                        </button>
                      </div>
                      <textarea
                        ref={htmlTextareaRef}
                        className="rawContent emailTemplateHtmlSource"
                        title="HTML email body"
                        placeholder="Enter HTML email body..."
                        value={form.htmlBody || ""}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateField("htmlBody", e.target.value)}
                      />
                    </div>
                    <div
                      className="contentEditSplitter"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize HTML source and preview panels"
                      onMouseDown={onSplitterMouseDown}
                    />
                    <div className="contentEditSection contentEditSectionPreview">
                      <h4>Rendered Preview</h4>
                      <div className="renderedContent emailTemplateHtmlPreview">
                        {previewHtml.trim() ? parseSanitizedHtml(previewHtml) : null}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <th className="label right">Text Body</th>
                <td>
                  <div className="emailTemplateSectionHeader">
                    <span className="emailTemplateSectionLabel">Plain Text</span>
                    <button type="button" className="emailSelectVariableBtn" onClick={() => openVariablePicker("textBody")}>
                      Select Variable
                    </button>
                  </div>
                  <textarea
                    ref={textTextareaRef}
                    className="emailTemplateTextBody"
                    rows={4}
                    title="Plain text email body"
                    placeholder="Optional plain text version..."
                    value={form.textBody || ""}
                    onChange={(e) => updateField("textBody", e.target.value)}
                  />
                  <p className="emailTemplateHint">Optional plain-text version of the email.</p>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="emailTemplateActions">
            <button type="button" onClick={props.onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="primary" onClick={saveAsync} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      <EmailContextVariableSelectModal
        isOpen={variablePickerField != null}
        onClose={() => setVariablePickerField(null)}
        onSelect={handleVariableSelected}
      />
    </>
  );
}
